import React from "react";
import styled from "@emotion/styled";
import { LandingPageRow } from "../LandingPageSection";
import { CustomThemeType } from "../../../../context/Providers";
import SectionFooter from "../SectionFooter";

const PainPointWrapper = styled.div<{ theme?: CustomThemeType }>`
    width: min(1400px, 96%);
    background: ${p => p.theme.brand};
    color: ${p => p.theme.brandContrast};
    border-radius: 32px;
    padding: clamp(2.5rem, 5vw, 4rem) clamp(1.5rem, 3vw, 3rem);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: clamp(2rem, 5vw, 3.5rem);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);

    @media (max-width: 1200px) {
        width: min(1100px, 95%);
    }

    @media (max-width: 1024px) {
        padding: clamp(2rem, 6vw, 3rem);
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const PainPointRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: clamp(2rem, 5vw, 3.5rem);

    @media (max-width: 1024px) {
        flex-direction: column;
    }
`;

const CopyColumn = styled.div<{ theme?: CustomThemeType }>`
    flex: 1 1 0;
    max-width: 65%;
    display: flex;
    flex-direction: column;
    gap: clamp(1.5rem, 2.5vw, 2.5rem);
    padding-right: clamp(0.75rem, 3vw, 1.75rem);

    @media (max-width: 1200px) {
        max-width: 100%;
        padding-right: 0;
    }
`;

const Headline = styled.h2<{ theme?: CustomThemeType }>`
    margin: 0;
    font-size: clamp(2.6rem, 5.2vw, 4.25rem);
    line-height: 1.02;
    font-weight: 600;
`;

const SubCopy = styled.p<{ theme?: CustomThemeType }>`
    margin: 0;
    font-size: clamp(1.25rem, 2.6vw, 1.5rem);
    line-height: 1.6;
    color: color-mix(in srgb, ${p => p.theme.brandContrast}, transparent 20%);
`;

const PainList = styled.ul<{ theme?: CustomThemeType }>`
    margin: 0;
    padding-left: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    color: ${p => p.theme.brandContrast};
    font-size: clamp(1.15rem, 2.4vw, 1.4rem);
`;

const PainListItem = styled.li<{ theme?: CustomThemeType }>`
    line-height: 1.5;
`;

const PhotoColumn = styled.div<{ theme?: CustomThemeType }>`
    flex: 0 0 clamp(280px, 28vw, 360px);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (max-width: 1024px) {
        flex: 1 1 auto;
        width: 100%;
    }
`;

const ImpactColumn = styled.div<{ theme?: CustomThemeType }>`
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    justify-content: center;
`;

const ImpactHeading = styled.h3<{ theme?: CustomThemeType }>`
    margin: 0;
    font-size: clamp(1.8rem, 3vw, 2.4rem);
    line-height: 1.2;
    font-weight: 600;
`;

const ImpactCopy = styled.p<{ theme?: CustomThemeType }>`
    margin: 0;
    font-size: clamp(1.1rem, 2.2vw, 1.35rem);
    line-height: 1.6;
    color: color-mix(in srgb, ${p => p.theme.brandContrast}, transparent 18%);
`;

const PhotoCard = styled.figure<{ theme?: CustomThemeType }>`
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: ${p => p.theme.background};
    border: 1px solid color-mix(in srgb, ${p => p.theme.foreground}, transparent 85%);
    border-radius: 24px;
    padding: 1rem;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.15);
`;

const PhotoLabel = styled.figcaption<{ theme?: CustomThemeType }>`
    font-size: 0.95rem;
    font-weight: 600;
    color: color-mix(in srgb, ${p => p.theme.foreground}, transparent 15%);
`;

const PhotoImage = styled("img", {
    shouldForwardProp: (prop) => prop !== "focus"
})<{ focus?: "left" | "center" | "right" }>`
    width: 100%;
    border-radius: 18px;
    object-fit: cover;
    object-position: ${p => {
        if (p.focus === "left") return "left center";
        if (p.focus === "right") return "right center";
        return "center";
    }};
`;

const SectionPainPointContent: React.FC = () => {
    return (
        <>
            <LandingPageRow height="auto">
                <PainPointWrapper>
                    <PainPointRow>
                        <CopyColumn>
                            <Headline>
                                Hate losing cabinets?
                            </Headline>
                            <SubCopy>
                                It happens more often than anyone admits.
                            </SubCopy>
                            <PainList>
                                <PainListItem>Sometimes someone offloading just starts grabbing cabinets.</PainListItem>
                                <PainListItem>Sometimes it's loaded out of order and no one unloading catches it.</PainListItem>
                                <PainListItem>Sometimes an exhausted driver simply misreads a label.</PainListItem>
                            </PainList>
                            <SubCopy>
                                Each mistake can cost thousands to fix -- and paper checklists won't warn you before it happens.
                            </SubCopy>
                        </CopyColumn>
                        <PhotoColumn>
                            <PhotoCard>
                                <PhotoImage src="/assets/painPoints/Truck%20Unload.png" alt="Busy truck unload with cabinets being moved chaotically" />
                                <PhotoLabel>Truck unloads get chaotic without scan prompts.</PhotoLabel>
                            </PhotoCard>
                        </PhotoColumn>
                    </PainPointRow>
                    <PainPointRow>
                        <PhotoColumn>
                            <PhotoCard>
                                <PhotoImage
                                    src="/assets/painPoints/Confusion.png"
                                    alt="Confused customer looking at missing cabinets"
                                    focus="left"
                                />
                                <PhotoLabel>You're forced to spend time and money fixing it.</PhotoLabel>
                            </PhotoCard>
                        </PhotoColumn>
                        <ImpactColumn>
                            <ImpactHeading>Every missed cabinet ripples through the entire job.</ImpactHeading>
                            <ImpactCopy>By the time anyone notices, install crews are waiting, customers are calling, and you're scrambling for replacements.</ImpactCopy>
                            <ImpactCopy>Overtime, re-deliveries, and appeasing upset clients eat away at the profit you worked for.</ImpactCopy>
                            <ImpactCopy>Cabinet Logger alerts your team before the truck leaves so you keep the schedule -- and the trust -- intact.</ImpactCopy>
                        </ImpactColumn>
                    </PainPointRow>
                </PainPointWrapper>
            </LandingPageRow>
            <SectionFooter sectionIndex={1} />
        </>
    );
};

export default SectionPainPointContent;

